import React, { useState, useEffect } from 'react';
import { TextField, FormControl, Box, Autocomplete } from '@mui/material';
import { UserTemplateItem } from '../../types/types';
import { useTemplatesList } from '@features/user';
import { useEventTracking } from '@services/statsig/hooks';

interface TemplatePickerProps {
  onTemplateSelect: (template: UserTemplateItem | undefined) => void;
  initialTemplate?: UserTemplateItem;
  initialTemplateId?: string | null;
  required?: boolean;
  label?: string;
}

const TemplatePicker: React.FC<TemplatePickerProps> = ({
  onTemplateSelect,
  initialTemplate,
  initialTemplateId,
  required = false,
  label = 'Choose a template',
}) => {
  const templatesList = useTemplatesList();
  const [selectedTemplate, setSelectedTemplate] = useState<UserTemplateItem | undefined>(
    initialTemplate,
  );
  const { template: templateMetrics } = useEventTracking();

  const handleTemplateChange = (event: any, value: UserTemplateItem | null) => {
    if (value) {
      setSelectedTemplate(value);
      onTemplateSelect(value);
      
      // Track template selection
      templateMetrics.select(value.template_id, {
        is_custom: value.type === 'user',
      });
    } else {
      setSelectedTemplate(undefined);
      onTemplateSelect(undefined);
    }
  };

  useEffect(() => {
    if (initialTemplateId) {
      const template = templatesList?.find(t => t.template_id === initialTemplateId);
      if (template) {
        setSelectedTemplate(template);
      }
    }
  }, [initialTemplateId, templatesList]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <FormControl fullWidth>
        {templatesList && (
          <Autocomplete
            options={templatesList}
            value={selectedTemplate || null}
            getOptionLabel={option => (option.display_name ? option.display_name : '')}
            renderInput={params => (
              <TextField {...params} label={label} variant="outlined" required={required} />
            )}
            onChange={handleTemplateChange}
          />
        )}
      </FormControl>
    </Box>
  );
};

export default TemplatePicker;
